/**
 * Program Marketplace Component
 * CBO marketplace to discover and apply to compatible sponsor programs
 * Shows match scores and predicted outcomes
 * Trust Marketplace - Program-CBO Matching System
 */

import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Progress } from '../ui/progress';
import { getWeaveinApiUrl } from '@/src/config';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { authFetch } from '@/lib/authFetch';
import { useTabbedChrome } from '@/components/weavein';
import {
  Search,
  Building2,
  MapPin,
  DollarSign,
  TrendingUp,
  Award,
  Filter,
  CheckCircle2,
  AlertCircle,
  Star,
  Users,
  Target,
  Send
} from 'lucide-react';

interface ProgramListing {
  id: number;
  name: string;
  program_type: string;
  description: string;
  sponsor_id: number;
  sponsor_name: string;
  is_active: boolean;
  start_date: string;
  end_date: string;
  capacity: number;
  funding_source: string;
  reimbursement_rate: number;
  services_offered: string[];
  required_services: string[];
  target_sdoh_areas: string[];
  min_success_rate: number;
  required_certifications: string[];
  match_score: number | null;
  match_quality: string | null;
}

interface ProgramMarketplaceProps {
  cboId: number;
}

const ProgramMarketplace: React.FC<ProgramMarketplaceProps> = ({ cboId }) => {
  const { t } = useAppTranslation();
  const tabbed = useTabbedChrome();
  const [programs, setPrograms] = useState<ProgramListing[]>([]);
  const [filteredPrograms, setFilteredPrograms] = useState<ProgramListing[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [programTypeFilter, setProgramTypeFilter] = useState<string>('');
  const [minMatchScore, setMinMatchScore] = useState<number>(0);

  // Application modal
  const [selectedProgram, setSelectedProgram] = useState<ProgramListing | null>(null);
  const [showApplicationModal, setShowApplicationModal] = useState(false);
  const [applicationMessage, setApplicationMessage] = useState('');
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    fetchPrograms();
  }, [cboId, minMatchScore]);

  useEffect(() => {
    applyFilters();
  }, [programs, searchTerm, programTypeFilter]);

  const fetchPrograms = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        cbo_id: cboId.toString(),
        ...(minMatchScore > 0 && { min_match_score: minMatchScore.toString() })
      });

      const url = getWeaveinApiUrl(`/api/marketplace/programs?${params}`);

      const response = await authFetch(url);

      if (!response.ok) {
        throw new Error(`Failed to fetch programs: ${response.status}`);
      }

      const data = await response.json();
      // Filter out any null/undefined entries from API response
      const validPrograms = Array.isArray(data) ? data.filter(p => p && p.id) : [];
      setPrograms(validPrograms);
      setFilteredPrograms(validPrograms);
    } catch (err) {
      console.error('Error fetching programs:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch programs');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...programs].filter(p => p && p.id); // Remove null/undefined programs

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(p =>
        p && (
          p.name?.toLowerCase().includes(term) ||
          p.sponsor_name?.toLowerCase().includes(term) ||
          p.description?.toLowerCase().includes(term)
        )
      );
    }

    // Program type filter
    if (programTypeFilter) {
      filtered = filtered.filter(p => p && p.program_type === programTypeFilter);
    }

    setFilteredPrograms(filtered);
  };

  const handleApplyToProgram = async () => {
    if (!selectedProgram) return;

    try {
      setApplying(true);
      const url = getWeaveinApiUrl(`/api/marketplace/programs/${selectedProgram.id}/apply`);

      const response = await authFetch(url, {
        method: 'POST',
        jsonBody: {
          cbo_id: cboId,
          program_id: selectedProgram.id,
          application_message: applicationMessage
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to submit application');
      }

      const result = await response.json();

      // Show success message
      alert(result.auto_approved
        ? `Application auto-approved! You're now enrolled in ${selectedProgram.name}`
        : `Application submitted successfully! Awaiting sponsor review.`
      );

      setShowApplicationModal(false);
      setSelectedProgram(null);
      setApplicationMessage('');
      fetchPrograms(); // Refresh programs

    } catch (err) {
      console.error('Error applying to program:', err);
      alert(err instanceof Error ? err.message : 'Failed to submit application');
    } finally {
      setApplying(false);
    }
  };

  const getMatchQualityColor = (quality: string) => {
    switch (quality) {
      case 'EXCELLENT':
        return 'bg-green-100 text-green-800 border-green-300';
      case 'GOOD':
        return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'FAIR':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'POOR':
        return 'bg-red-100 text-red-800 border-red-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getMatchQualityIcon = (quality: string) => {
    switch (quality) {
      case 'EXCELLENT':
        return <CheckCircle2 className="w-4 h-4" />;
      case 'GOOD':
        return <Star className="w-4 h-4" />;
      case 'FAIR':
        return <AlertCircle className="w-4 h-4" />;
      default:
        return null;
    }
  };

  const programTypes = [...new Set(programs.filter(p => p && p.program_type).map(p => p.program_type))];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <Card className="p-6">
        <div className="flex items-center gap-2 text-red-600">
          <AlertCircle className="w-5 h-5" />
          <p>Error: {error}</p>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          {!tabbed && <h1 className="text-3xl font-bold text-gray-900">{t('marketplace.program_marketplace', 'Program Marketplace')}</h1>}
          <p className="text-gray-600 mt-1">{t('marketplace.discover_and_apply_to_compatible', 'Discover and Apply to Compatible Programs')}</p>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Building2 className="w-4 h-4" />
          <span>{filteredPrograms.length} programs available</span>
        </div>
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="md:col-span-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder={t('marketplace.search_programs_or_sponsors', 'Search programs or sponsors...')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Program Type */}
          <div>
            <select
              value={programTypeFilter}
              onChange={(e) => setProgramTypeFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">{t('marketplace.all_program_types', 'All Program Types')}</option>
              {programTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>

          {/* Match Score */}
          <div>
            <select
              value={minMatchScore}
              onChange={(e) => setMinMatchScore(parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="0">{t('marketplace.all_matches', 'All Matches')}</option>
              <option value="90">Excellent Match (90+)</option>
              <option value="75">{t('marketplace.good_match_75', 'Good Match (75+)')}</option>
              <option value="60">{t('marketplace.fair_match_60', 'Fair Match (60+)')}</option>
            </select>
          </div>
        </div>
      </Card>

      {/* Programs Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {filteredPrograms.filter(program => program && program.id).map((program) => {
          // Defensive null check - skip if program is somehow null
          if (!program || !program.id) return null;

          return (
          <Card key={program.id} className="p-6 hover:shadow-lg transition-shadow">
            {/* Header */}
            <div className="flex justify-between items-start mb-4">
              <div className="flex-1">
                <h3 className="text-xl font-semibold text-gray-900 mb-1">{program.name}</h3>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Building2 className="w-4 h-4" />
                  <span>{program.sponsor_name || 'Unknown Sponsor'}</span>
                </div>
              </div>
              {program.match_score !== null && program.match_score !== undefined && (
                <div className="flex flex-col items-end gap-2">
                  {program.match_quality && (
                    <div className={`px-3 py-1 rounded-full border flex items-center gap-1 ${getMatchQualityColor(program.match_quality)}`}>
                      {getMatchQualityIcon(program.match_quality)}
                      <span className="text-xs font-medium">{program.match_quality}</span>
                    </div>
                  )}
                  <div className="text-2xl font-bold text-blue-600">{program.match_score}%</div>
                </div>
              )}
            </div>

            {/* Description */}
            {program.description && (
              <p className="text-gray-600 text-sm mb-4 line-clamp-2">{program.description}</p>
            )}

            {/* Details */}
            <div className="space-y-2 mb-4">
              <div className="flex items-center gap-2 text-sm">
                <Badge variant="secondary">{program.program_type}</Badge>
                {program.funding_source && (
                  <Badge variant="outline">{program.funding_source}</Badge>
                )}
              </div>

              {program.reimbursement_rate != null && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <DollarSign className="w-4 h-4" />
                  <span>${Number(program.reimbursement_rate).toFixed(2)} reimbursement rate</span>
                </div>
              )}

              {program.capacity && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Users className="w-4 h-4" />
                  <span>Capacity: {program.capacity} members</span>
                </div>
              )}
            </div>

            {/* Services */}
            {program.services_offered && program.services_offered.length > 0 && (
              <div className="mb-4">
                <div className="text-xs font-medium text-gray-500 mb-2">{t('marketplace.services_offered', 'Services Offered:')}</div>
                <div className="flex flex-wrap gap-1">
                  {program.services_offered.slice(0, 3).map((service, idx) => (
                    <Badge key={idx} variant="outline" className="text-xs">{service}</Badge>
                  ))}
                  {program.services_offered.length > 3 && (
                    <Badge variant="outline" className="text-xs">+{program.services_offered.length - 3} more</Badge>
                  )}
                </div>
              </div>
            )}

            {/* Match Details */}
            {program.match_score !== null && program.match_score !== undefined && (
              <div className="mb-4">
                <div className="flex justify-between text-xs text-gray-600 mb-1">
                  <span>{t('marketplace.compatibility', 'Compatibility')}</span>
                  <span>{program.match_score}%</span>
                </div>
                <Progress value={program.match_score} className="h-2" />
              </div>
            )}

            {/* Requirements */}
            {program.min_success_rate && (
              <div className="mb-4 p-3 bg-gray-50 rounded-md">
                <div className="text-xs font-medium text-gray-700 mb-2">{t('marketplace.requirements', 'Requirements:')}</div>
                <div className="text-xs text-gray-600 space-y-1">
                  <div className="flex items-center gap-1">
                    <Target className="w-3 h-3" />
                    <span>Min. {program.min_success_rate}% success rate</span>
                  </div>
                  {program.required_certifications && program.required_certifications.length > 0 && (
                    <div className="flex items-center gap-1">
                      <Award className="w-3 h-3" />
                      <span>Certifications: {program.required_certifications.join(', ')}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Actions */}
            <Button
              onClick={() => {
                setSelectedProgram(program);
                setShowApplicationModal(true);
              }}
              className="w-full"
              variant={program.match_score !== null && program.match_score !== undefined && program.match_score >= 75 ? 'default' : 'outline'}
            >
              <Send className="w-4 h-4 mr-2" />
              {t('marketplace.apply_to_program', 'Apply to Program')}
            </Button>
          </Card>
          );
        })}
      </div>

      {filteredPrograms.length === 0 && (
        <Card className="p-12 text-center">
          <Building2 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-700 mb-2">{t('marketplace.no_programs_found', 'No Programs Found')}</h3>
          <p className="text-gray-600">
            {searchTerm || programTypeFilter
              ? 'Try adjusting your filters to see more programs.'
              : 'No programs are currently available in the marketplace.'}
          </p>
        </Card>
      )}

      {/* Application Modal */}
      <Dialog open={showApplicationModal} onOpenChange={setShowApplicationModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Apply to {selectedProgram?.name}</DialogTitle>
            <DialogDescription>
              Submit your application to join this program. {selectedProgram?.match_score && selectedProgram?.match_score >= 70
                ? 'Your high match score may qualify for auto-approval!'
                : 'Your application will be reviewed by the sponsor.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Match Score Display */}
            {selectedProgram?.match_score !== null && selectedProgram?.match_score !== undefined && (
              <div className="p-4 bg-blue-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-gray-900">{t('marketplace.your_match_score', 'Your Match Score')}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-2xl font-bold text-blue-600">{selectedProgram.match_score}%</span>
                    {selectedProgram.match_quality && (
                      <Badge className={getMatchQualityColor(selectedProgram.match_quality)}>
                        {selectedProgram.match_quality}
                      </Badge>
                    )}
                  </div>
                </div>
                <Progress value={selectedProgram.match_score} className="h-2" />
              </div>
            )}

            {/* Application Message */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Application Message (Optional)
              </label>
              <textarea
                value={applicationMessage}
                onChange={(e) => setApplicationMessage(e.target.value)}
                placeholder={t('marketplace.tell_the_sponsor_why_youd', 'Tell the sponsor why you\'d like to join this program...')}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setShowApplicationModal(false);
                  setSelectedProgram(null);
                  setApplicationMessage('');
                }}
                disabled={applying}
              >
                {t('marketplace.cancel', 'Cancel')}
              </Button>
              <Button
                onClick={handleApplyToProgram}
                disabled={applying}
              >
                {applying ? (
                  <>
                    <LoadingSpinner className="w-4 h-4 mr-2" />
                    {t('marketplace.submitting', 'Submitting...')}
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    {t('marketplace.submit_application', 'Submit Application')}
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProgramMarketplace;
